import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import s from "./DateTimePicker.module.css";

/**
 * Premium date + time picker — mirrors DatePicker's portal/popover
 * idioms but with a 12-hour time row underneath the calendar grid.
 *
 * Value contract: ISO "YYYY-MM-DDTHH:MM" — the same shape the native
 * <input type="datetime-local"> emits, so swapping it in is a drop-in.
 * Empty value is represented by "".
 *
 * Props: value, onChange(iso), min (ISO), max (ISO), className, placeholder
 */
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function parseISO(v) {
  if (!v || typeof v !== "string") return null;
  // Accept "YYYY-MM-DD" or "YYYY-MM-DDTHH:MM" or "YYYY-MM-DDTHH:MM:SS".
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/);
  if (!m) return null;
  return {
    y:  +m[1],
    mo: +m[2] - 1,
    d:  +m[3],
    h:  m[4] != null ? +m[4] : 0,
    mi: m[5] != null ? +m[5] : 0,
  };
}

function toISO(y, mo, d, h, mi) {
  const pad = n => String(n).padStart(2, "0");
  return `${y}-${pad(mo + 1)}-${pad(d)}T${pad(h)}:${pad(mi)}`;
}

function daysInMonth(y, mo) {
  return new Date(y, mo + 1, 0).getDate();
}

function formatDisplay(iso) {
  const p = parseISO(iso);
  if (!p) return "";
  const hr12 = ((p.h + 11) % 12) + 1;
  const ampm = p.h >= 12 ? "PM" : "AM";
  return `${MONTHS_SHORT[p.mo]} ${p.d}, ${p.y} · ${String(hr12).padStart(2,"0")}:${String(p.mi).padStart(2,"0")} ${ampm}`;
}

/** Snap minutes to the nearest interval — 5 by default. Avoids the
 *  endless-scroll feel of 0–59 while still letting the user land on
 *  any common appointment time. */
const MINUTE_STEP = 5;
function snapMinute(m) {
  const stepped = Math.round(m / MINUTE_STEP) * MINUTE_STEP;
  return Math.max(0, Math.min(59, stepped));
}

export default function DateTimePicker({
  value, onChange, min, max, className, placeholder = "Select date & time",
}) {
  const parsed = parseISO(value);
  const today = new Date();

  // View state — month / year currently shown on the calendar grid.
  const [viewY, setViewY] = useState(parsed?.y  ?? today.getFullYear());
  const [viewM, setViewM] = useState(parsed?.mo ?? today.getMonth());
  // Internal time state — the popover commits via onChange when the
  // user picks a date or clicks Done. Held separately so a user can
  // adjust hour/minute/ampm before committing.
  const [hour,   setHour]   = useState(parsed ? (((parsed.h + 11) % 12) + 1) : 12);
  const [minute, setMinute] = useState(parsed ? snapMinute(parsed.mi) : 0);
  const [ampm,   setAmpm]   = useState(parsed ? (parsed.h >= 12 ? "PM" : "AM") : "AM");

  const [open, setOpen]     = useState(false);

  const wrapRef     = useRef(null);
  const inputRowRef = useRef(null);
  const popupRef    = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const POPUP_WIDTH  = 340;
  const POPUP_HEIGHT = 460;

  function recomputeCoords() {
    const el = inputRowRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const overshootRight = (rect.left + POPUP_WIDTH) - (window.innerWidth - 8);
    const left = Math.max(8, rect.left - Math.max(0, overshootRight));
    const wouldOverflowBottom = rect.bottom + 8 + POPUP_HEIGHT > window.innerHeight;
    const top = wouldOverflowBottom
      ? Math.max(8, rect.top - 8 - POPUP_HEIGHT)
      : rect.bottom + 8;
    setCoords({ top, left });
  }

  // Re-sync internal state when the external value changes.
  useEffect(() => {
    const p = parseISO(value);
    if (p) {
      setViewY(p.y); setViewM(p.mo);
      setHour((((p.h + 11) % 12) + 1));
      setMinute(snapMinute(p.mi));
      setAmpm(p.h >= 12 ? "PM" : "AM");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Close on outside click — popup is portaled so check both refs.
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current?.contains(e.target))  return;
      if (popupRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // Coordinates synchronously before paint so the popup never flashes
  // at the wrong spot.
  useLayoutEffect(() => {
    if (!open) return;
    recomputeCoords();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener("resize", recomputeCoords);
    window.addEventListener("scroll",  recomputeCoords, true);
    return () => {
      window.removeEventListener("resize", recomputeCoords);
      window.removeEventListener("scroll",  recomputeCoords, true);
    };
  }, [open]);

  // Escape to close — matches every other popover in the app.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const minP = parseISO(min);
  const maxP = parseISO(max);

  /** Convert displayed (h12, ampm) back to 0-23 hour for ISO output. */
  function to24h(h12, ap) {
    let h = h12 % 12;          // 12 → 0
    if (ap === "PM") h += 12;  // 0-11 → 12-23
    return h;
  }

  function isDateDisabled(y, mo, d) {
    const t = new Date(y, mo, d).getTime();
    if (minP && t < new Date(minP.y, minP.mo, minP.d).getTime()) return true;
    if (maxP && t > new Date(maxP.y, maxP.mo, maxP.d).getTime()) return true;
    return false;
  }

  function commit(y, mo, d, h12, mi, ap) {
    onChange(toISO(y, mo, d, to24h(h12, ap), mi));
  }

  function pickDay(d) {
    if (isDateDisabled(viewY, viewM, d)) return;
    // Pick + immediate commit (with current time). Keep popover open so
    // the user can fine-tune the time before closing.
    commit(viewY, viewM, d, hour, minute, ampm);
  }

  function bumpHour(delta) {
    let h = hour + delta;
    if (h > 12) h = 1;
    if (h < 1)  h = 12;
    setHour(h);
    if (parsed) commit(parsed.y, parsed.mo, parsed.d, h, minute, ampm);
  }
  function bumpMinute(delta) {
    let m = minute + delta * MINUTE_STEP;
    if (m >= 60) m -= 60;
    if (m < 0)   m += 60;
    setMinute(m);
    if (parsed) commit(parsed.y, parsed.mo, parsed.d, hour, m, ampm);
  }
  function toggleAmpm(next) {
    setAmpm(next);
    if (parsed) commit(parsed.y, parsed.mo, parsed.d, hour, minute, next);
  }

  function prevMonth() {
    if (viewM === 0) { setViewM(11); setViewY(y => y - 1); }
    else setViewM(m => m - 1);
  }
  function nextMonth() {
    if (viewM === 11) { setViewM(0); setViewY(y => y + 1); }
    else setViewM(m => m + 1);
  }

  function clearAll() {
    onChange("");
    setOpen(false);
  }
  function pickToday() {
    const now = new Date();
    const h12 = ((now.getHours() + 11) % 12) + 1;
    const ap  = now.getHours() >= 12 ? "PM" : "AM";
    const mi  = snapMinute(now.getMinutes());
    setViewY(now.getFullYear()); setViewM(now.getMonth());
    setHour(h12); setMinute(mi); setAmpm(ap);
    commit(now.getFullYear(), now.getMonth(), now.getDate(), h12, mi, ap);
  }

  // Calendar grid with day-of-week leading blanks.
  const firstDow = new Date(viewY, viewM, 1).getDay();
  const totalDays = daysInMonth(viewY, viewM);
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(d);

  // Year range — 10 years either side covers any reasonable expires-at.
  const currentYear = today.getFullYear();
  const years = [];
  for (let y = currentYear + 10; y >= currentYear - 10; y--) years.push(y);

  const isSelectedDay = (d) =>
    parsed && parsed.y === viewY && parsed.mo === viewM && parsed.d === d;
  const isToday = (d) => {
    const t = new Date();
    return t.getFullYear() === viewY && t.getMonth() === viewM && t.getDate() === d;
  };

  return (
    <div className={`${s.wrap} ${className || ""}`} ref={wrapRef}>
      <button
        type="button"
        className={`${s.trigger} ${open ? s.triggerOpen : ""}`}
        ref={inputRowRef}
        onClick={() => { if (!open) recomputeCoords(); setOpen(o => !o); }}
        aria-haspopup="dialog"
        aria-expanded={open}>
        <svg className={s.triggerIcon} width="16" height="16" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="2"
             strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="4" width="18" height="18" rx="3"/>
          <path d="M16 2v4M8 2v4M3 10h18"/>
          <circle cx="12" cy="15" r="3"/>
          <path d="M12 13.5v1.5l1 .5"/>
        </svg>
        <span className={value ? s.triggerValue : s.triggerPlaceholder}>
          {value ? formatDisplay(value) : placeholder}
        </span>
        {value && (
          <span
            role="button"
            tabIndex={0}
            className={s.triggerClear}
            onClick={(e) => { e.stopPropagation(); clearAll(); }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault(); e.stopPropagation(); clearAll();
              }
            }}
            aria-label="Clear">×</span>
        )}
      </button>

      {open && createPortal(
        <div
          className={s.popover}
          role="dialog"
          aria-modal="false"
          aria-label="Choose date and time"
          ref={popupRef}
          style={{ top: coords.top, left: coords.left }}>

          {/* Calendar header — prev/month-year/next */}
          <header className={s.head}>
            <button type="button" className={s.navBtn} onClick={prevMonth}
                    aria-label="Previous month">‹</button>
            <div className={s.headPickers}>
              <select className={s.monthSelect}
                      value={viewM}
                      onChange={e => setViewM(+e.target.value)}>
                {MONTHS.map((name, i) => <option key={i} value={i}>{name}</option>)}
              </select>
              <select className={s.yearSelect}
                      value={viewY}
                      onChange={e => setViewY(+e.target.value)}>
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <button type="button" className={s.navBtn} onClick={nextMonth}
                    aria-label="Next month">›</button>
          </header>

          <div className={s.dowRow}>
            {DAYS.map(d => <span key={d} className={s.dow}>{d}</span>)}
          </div>

          <div className={s.grid}>
            {cells.map((d, i) => {
              if (d == null) return <span key={i} className={s.cellBlank} />;
              const sel = isSelectedDay(d);
              const tdy = isToday(d);
              const disabled = isDateDisabled(viewY, viewM, d);
              return (
                <button
                  key={i}
                  type="button"
                  className={`${s.cell} ${sel ? s.cellSel : ""} ${tdy ? s.cellToday : ""} ${disabled ? s.cellDisabled : ""}`}
                  onClick={() => pickDay(d)}
                  disabled={disabled}>
                  {d}
                </button>
              );
            })}
          </div>

          {/* Time row — hour, minute, AM/PM. Up/Down buttons on each
              numeric, segmented control for AM/PM. */}
          <div className={s.timeRow}>
            <div className={s.timeLabel}>Time</div>
            <div className={s.timeControls}>
              <TimeSpinner
                value={String(hour).padStart(2, "0")}
                onUp={() => bumpHour(+1)}
                onDown={() => bumpHour(-1)}
                aria="Hour" />
              <span className={s.timeColon}>:</span>
              <TimeSpinner
                value={String(minute).padStart(2, "0")}
                onUp={() => bumpMinute(+1)}
                onDown={() => bumpMinute(-1)}
                aria="Minute" />
              <div className={s.ampm} role="tablist" aria-label="AM or PM">
                <button
                  type="button"
                  role="tab"
                  aria-selected={ampm === "AM"}
                  className={`${s.ampmBtn} ${ampm === "AM" ? s.ampmBtnActive : ""}`}
                  onClick={() => toggleAmpm("AM")}>AM</button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={ampm === "PM"}
                  className={`${s.ampmBtn} ${ampm === "PM" ? s.ampmBtnActive : ""}`}
                  onClick={() => toggleAmpm("PM")}>PM</button>
              </div>
            </div>
          </div>

          <footer className={s.foot}>
            <button type="button" className={s.footBtn} onClick={clearAll}>Clear</button>
            <button type="button" className={s.footBtn} onClick={pickToday}>Now</button>
            <button type="button" className={s.footBtnPrimary} onClick={() => setOpen(false)}>
              Done
            </button>
          </footer>
        </div>,
        document.body
      )}
    </div>
  );
}

function TimeSpinner({ value, onUp, onDown, aria }) {
  return (
    <div className={s.spinner} role="group" aria-label={aria}>
      <button type="button" className={s.spinBtn} onClick={onUp} aria-label={`${aria} up`}>▲</button>
      <div className={s.spinValue} aria-live="polite">{value}</div>
      <button type="button" className={s.spinBtn} onClick={onDown} aria-label={`${aria} down`}>▼</button>
    </div>
  );
}
